import { useMediaQuery } from "react-responsive";
import { ProfileLinkPersonalInfoConfig } from "../../client";
import { ImageUpload, ProfileComponentProps } from "../../types";
import { useStatsigClient } from "../../hooks";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import {
  isString,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components";
import ProfilePhoto from "../../components/ProfilePhoto";
import { EditorInput } from "../../components/input/EditorInput";
import { DynamicInput } from "../../components/input/DynamicInput";
import { cn } from "../../components/utils/cn";
import { useState } from "react";
import { ImageCropperDialog } from "../../components/ImageCropperDialog";

interface ProfilePersonalInfoProps extends ProfileComponentProps {
  name: string;
  title: string;
  pitch: string;
  editedPitch?: string;
  profilePhotoId?: string;
  isEditable: boolean;
  useProfilePicture: boolean;
  hidePersonalInfo: boolean;
  roundedProfilePicture: boolean;
  useBorder: boolean;
  onShowCropTool: (image: any) => void;
  onProfileUpdate: (name: string, title: string, pitch: string) => void;
  hidePitch?: boolean;
  logoAssetUrl?: string | null;
  logoAssetSize?: number | null;
  updatedPhotoId?: string | null;
  isUploadingPhoto: boolean;
  logoUrl?: string | null;
  children?: React.ReactNode;
  removePadding?: boolean;
  profileLinkPersonalInfoConfig?: ProfileLinkPersonalInfoConfig;
  /**
   * When provided, the pitch is inline-editable and commits (on blur) to this
   * callback instead of the master profile — used by the template editor to edit
   * the per-link `editedPitch`. Independent of `isEditable` (which edits master).
   */
  onEditPitch?: (value: string) => void;
  /** Same as onEditPitch, for the name / title (per-link overrides). */
  onEditName?: (value: string) => void;
  onEditTitle?: (value: string) => void;
}

function ProfilePersonalInfo({
  name: initialName,
  title: initialTitle,
  pitch: initialPitch,
  editedPitch,
  profilePhotoId,
  isEditable,
  useProfilePicture,
  hidePersonalInfo,
  onShowCropTool,
  onProfileUpdate,
  isDarkModeActive,
  roundedProfilePicture,
  useBorder,
  hidePitch,
  logoAssetUrl,
  logoAssetSize,
  children,
  updatedPhotoId,
  isUploadingPhoto,
  removePadding,
  logoUrl,
  profileLinkPersonalInfoConfig,
  onEditPitch,
  onEditName,
  onEditTitle,
}: ProfilePersonalInfoProps) {
  const isMobile = useMediaQuery({ query: "(max-width: 760px)" });
  const statsigClient = useStatsigClient();
  const [name, setName] = useState(initialName);
  const [title, setTitle] = useState(initialTitle);
  const [pitch, setPitch] = useState(initialPitch);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");

  const handleNameBlur = (newName: string) => {
    onProfileUpdate(newName, title, pitch);
  };

  const handleTitleBlur = (newTitle: string) => {
    onProfileUpdate(name, newTitle, pitch);
  };

  const handlePitchBlur = (newPitch: string) => {
    onProfileUpdate(name, title, newPitch);
  };

  const handleImageSelected = (imageUpload: ImageUpload) => {
    if (imageUpload.base64File) {
      setImageToCrop(imageUpload.base64File);
      setShowCropper(true);
    }
  };

  const handleExistingImageSelected = (imageUpload: ImageUpload) => {
    if (imageUpload.base64File) {
      setImageToCrop(imageUpload.base64File);
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedImage: string) => {
    const imageUpload: ImageUpload = {
      fileType: "image/jpeg",
      base64File: croppedImage.split(",")[1],
    };
    onShowCropTool(imageUpload);
    setShowCropper(false);
  };

  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        marginBottom: "30px",
      }}
      className={cn(removePadding ? "" : "pl-4")}
    >
      {!profileLinkPersonalInfoConfig ||
      (profileLinkPersonalInfoConfig &&
        profileLinkPersonalInfoConfig.showPersonalInfo) ? (
        <div className="flex flex-row justify-between w-full">
          <div className="flex-1 flex flex-row w-full">
            {useProfilePicture ? (
              !profileLinkPersonalInfoConfig && isEditable ? (
                <TooltipProvider key={1} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <ProfilePhoto
                          key="profile-photo"
                          isLoading={isUploadingPhoto}
                          id="profile"
                          photoAssetId={
                            updatedPhotoId != null
                              ? updatedPhotoId
                              : profilePhotoId
                          }
                          round={
                            roundedProfilePicture ||
                            !profileLinkPersonalInfoConfig
                          }
                          useBorder={false}
                          allowEdit={isEditable}
                          onImageSelected={handleImageSelected}
                          onExistingImageSelected={handleExistingImageSelected}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="mb-2">
                      Click to edit
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : !hidePersonalInfo ? (
                <ProfilePhoto
                  key="profile-photo"
                  isLoading={isUploadingPhoto}
                  id="profile"
                  photoAssetId={
                    updatedPhotoId != null ? updatedPhotoId : profilePhotoId
                  }
                  useBorder={useBorder}
                  round={roundedProfilePicture ?? true}
                  allowEdit={isEditable}
                  onImageSelected={handleImageSelected}
                  onExistingImageSelected={handleExistingImageSelected}
                />
              ) : null
            ) : (
              <span />
            )}

            {!hidePersonalInfo && (
              <div
                className={
                  "flex flex-col min-h-[100%] w-full " +
                  (useProfilePicture ? "pl-4" : "") +
                  (children == null ? " justify-center" : " justify-between")
                }
              >
                <div className="flex flex-row items-center justify-between">
                  {onEditName || onEditTitle ? (
                    <div className="flex flex-col w-full">
                      <DynamicInput
                        disabled={false}
                        label="Full name"
                        value={initialName}
                        validate={isString}
                        rows={1}
                        editable={true}
                        inputClassName="text-[18px] font-semibold"
                        onBlur={(
                          event: React.FocusEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => onEditName?.(event.target.value)}
                        type="textarea"
                      />
                      <DynamicInput
                        disabled={false}
                        label="Title"
                        value={initialTitle}
                        validate={isString}
                        rows={1}
                        editable={true}
                        inputClassName="text-[14px] font-normal"
                        onBlur={(
                          event: React.FocusEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => onEditTitle?.(event.target.value)}
                        type="textarea"
                      />
                    </div>
                  ) : isEditable ? (
                    <div className="flex flex-col">
                      <EditorInput
                        disabled={!isEditable}
                        label="Full name"
                        value={name}
                        onChange={(event) => {
                          const inputValue = event.target.value;
                          setName(inputValue);
                        }}
                        onFinishWriting={(value) => {
                          handleNameBlur(value);
                        }}
                        className="text-[18px] font-semibold"
                      />
                      <EditorInput
                        disabled={!isEditable}
                        label="Title"
                        value={title}
                        onChange={(event) => {
                          const inputValue = event.target.value;
                          setTitle(inputValue);
                        }}
                        onFinishWriting={(value) => {
                          handleTitleBlur(value);
                        }}
                        className="text-[14px] font-normal w-full"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <span className="text-[18px] font-semibold">{name}</span>
                      <span className="text-[14px] font-normal">{title}</span>
                    </div>
                  )}
                  {logoUrl && (
                    <img
                      alt="logo"
                      src={logoUrl}
                      className={`object-scale-contain mr-2`}
                      style={{
                        maxHeight: 60,
                      }}
                    />
                  )}
                </div>
                {children ?? null}
              </div>
            )}
          </div>
          {logoAssetUrl && !isMobile ? (
            <img
              key="org-logo-pdf"
              src={
                logoAssetUrl.length > 100
                  ? logoAssetUrl
                  : "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                    logoAssetUrl +
                    "?nocache=12345"
              }
              className={`object-scale-contain`}
              alt="logo"
              crossOrigin="anonymous"
              style={{ maxHeight: logoAssetSize ?? 40 }}
            />
          ) : null}
        </div>
      ) : null}
      <div style={{ marginTop: 15, minWidth: "100%" }}>
        {hidePitch === false && onEditPitch ? (
          <DynamicInput
            className={cn(
              "text-sm whitespace-pre-line",
              "transition-all duration-300 overflow-hidden",
            )}
            inputClassName="text-sm"
            disabled={false}
            label="Describe yourself, your profession, your passion or anything you would like to share with potential colleagues"
            value={editedPitch || pitch}
            validate={isString}
            rows={1}
            editable={true}
            onBlur={(
              event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => onEditPitch(event.target.value)}
            type="textarea"
          />
        ) : hidePitch == false && !(!isEditable && pitch === "") ? (
          isEditable ? (
            <DynamicInput
              className={cn(
                "text-sm whitespace-pre-line",
                "transition-all duration-300 overflow-hidden",
              )}
              inputClassName="text-sm"
              disabled={!isEditable}
              label="Describe yourself, your profession, your passion or anything you would like to share with potential colleagues"
              value={pitch}
              rows={1}
              onBlur={(
                event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => {
                const inputValue = event.target.value;
                handlePitchBlur(inputValue);
              }}
              onChange={(event) => {
                const inputValue = event.target.value;
                setPitch(inputValue);
              }}
              editable={isEditable}
              onFinishWriting={(value) => {
                handlePitchBlur(value);
              }}
              type="textarea"
            />
          ) : (
            <div className="text-sm whitespace-pre-line">
              {editedPitch || pitch}
            </div>
          )
        ) : null}
      </div>

      {showCropper && (
        <ImageCropperDialog
          open={showCropper}
          onClose={() => setShowCropper(false)}
          image={imageToCrop}
          onCrop={handleCropComplete}
          aspectRatio={1}
        />
      )}
    </div>
  );
}

export default ProfilePersonalInfo;
